import {SpinePlayer} from "../../../../core/displayobject/SpinePlayer";
import {Helper} from "../../../../core/utils/Helper";
import {StateHandler} from "../../../../core/utils/StateHandler";
import {PunkRocker} from '../../../PunkRocker';
import {PREngineManager} from "../../../punkEngine/PREngineManager";
import {gsap} from "gsap";
import {SymbolMultiplyCounter} from "../symbol/SymbolMultiplyCounter";
import {LayoutDimData} from "../../../../core/utils/ResizeHelper";
import {GameConfig} from "../../../config/base/GameConfig";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {GameData} from "../../../server/data/GameData";
import {BaseServerData, jumpingWildConfig} from "../../../server/base/IData";
import {Sequence} from "../../../../core/displayobject/Sequence";
import TrackEntry = PIXI.spine.core.TrackEntry;

class WildItem extends PIXI.Container {
    ID: number;
    mainSpine: PIXI.spine.Spine;
    merged: boolean;
    mult: SymbolMultiplyCounter;
    timeline?: gsap.core.Timeline;
    jumped: boolean;
    seq: Sequence;

    constructor(id: number) {
        super();
        this.ID = id;
        this.init();
    }

    private _isBurning: boolean = false;

    get isBurning(): boolean {
        return this._isBurning;
    }

    set isBurning(value: boolean) {
        this._isBurning = value;
    }

    init(): void {
        this.mainSpine = this.addChild(new PIXI.spine.Spine(PunkRocker.assetLoader.getSpineAsset("JW_SPINE")));
        this.mainSpine.visible = false;

        this.mult = this.addChild(new SymbolMultiplyCounter(0));
        this.seq = this.addChild(Sequence.createSequence(PunkRocker.assetLoader.getTextures("JumpFire")));

        this.mult.visible = false;
        this.mult.pivot.set(0, 70);
        this.mult.scale.set(0.8, 0.8);
        this.mult.text = "";

        this.seq.scale.set(2, 2);
        this.seq.blendMode = PIXI.BLEND_MODES.ADD;
        this.seq.alpha = 0;
    }

    public resetSpine(showSpine: boolean = false): void {
        this.mainSpine.state.clearTracks();
        this.mainSpine.skeleton.setToSetupPose();
        this.mainSpine.skeleton.setBonesToSetupPose();
        this.mainSpine.skeleton.setSlotsToSetupPose();
        this.mainSpine.visible = showSpine;
    }
}

interface WildAnimationJump {
    ID: number;
    state: string;
    mult?: number;
    to: {
        reel: number,
        row: number
    },
    from: {
        reel: number,
        row: number
    },
    duration?: number;
}

export class JumpWildState extends StateHandler {
    public static readonly INACTIVE: string = "dead";
    public static readonly WIN: string = "win";
    public static readonly IDLE: string = "idle";
    public static readonly JUMP: string = "jump";
    public static readonly FALL_IN: string = "fall_in";
    public static readonly MERGE: string = "merge";
    public static readonly MERGE_WIN: string = "merge_Win";
    public static readonly MERGE_IDLE: string = "merge_Idle";

    public static readonly FEATURE_MOLOTOV_FS_GET: string = "molotov_get";
    public static readonly FEATURE_MOLOTOV_FS_LOOP: string = "molotov_loop";
    public static readonly FEATURE_MOLOTOV_FS_TRIGGER: string = "molotov_trigger";
    public static readonly FEATURE_MOLOTOV_FS_TRIGGER_FAIL: string = "molotov_trigger_dud";

    public static readonly FEATURE_PLUS_1_GET: string = "plus_1";
    public static readonly FEATURE_PLUS_1_LOOP: string = "idle";

    public static readonly FEATURE_ENABLE_TRAIN_GET: string = "train";
    public static readonly FEATURE_ENABLE_TRAIN_LOOP: string = "idle";

    public static readonly FEATURE_BJW_GET: string = "bruning_start";
    public static readonly FEATURE_BJW_LOOP: string = "burning_loop";

    public static readonly FEATURE_UPGRADE_GET: string = "upgrade_get";
    public static readonly FEATURE_UPGRADE_LOOP: string = "upgrade_loop";
    public static readonly FEATURE_UPGRADE_TRIGGER: string = "upgrade_trigger";

    public static readonly FEATURE_6X6_GET: string = "6x6";
    public static readonly FEATURE_6X6_LOOP: string = "merge_Idle";

    public static readonly FEATURE_MULT_GET: string = "merge";
    public static readonly FEATURE_MULT_LOOP: string = "merge_Idle";

    public static readonly FEATURE_XWAYS_FS_GET: string = "merge";
    public static readonly FEATURE_XWAYS_FS_LOOP: string = "merge_Idle";

    public SpineStateDuration(state: string): number {
        return SpinePlayer.fetchDuration("JW_SPINE", state);
    }

    public isSpineLoop(state: string): boolean {
        switch (state) {
            case JumpWildState.IDLE:
            case JumpWildState.MERGE_IDLE:
            case JumpWildState.FEATURE_MOLOTOV_FS_LOOP:
            case JumpWildState.FEATURE_PLUS_1_LOOP:
            case JumpWildState.FEATURE_ENABLE_TRAIN_LOOP:
            case JumpWildState.FEATURE_BJW_LOOP:
            case JumpWildState.FEATURE_UPGRADE_LOOP:
            case JumpWildState.FEATURE_6X6_LOOP:
            case JumpWildState.FEATURE_MULT_LOOP:
            case JumpWildState.FEATURE_XWAYS_FS_LOOP:
                return true;
            default:
                return false;
        }
    }

    public NextSpineState(state: string): string {
        switch (state) {
            case JumpWildState.MERGE_WIN:
                return JumpWildState.MERGE_IDLE;

            case JumpWildState.FEATURE_MOLOTOV_FS_GET:
                return JumpWildState.FEATURE_MOLOTOV_FS_LOOP;

            case JumpWildState.FEATURE_MOLOTOV_FS_TRIGGER:
            case JumpWildState.FEATURE_MOLOTOV_FS_TRIGGER_FAIL:
                return JumpWildState.MERGE_IDLE;

            case JumpWildState.FEATURE_PLUS_1_GET:
                return JumpWildState.FEATURE_PLUS_1_LOOP;

            case JumpWildState.FEATURE_ENABLE_TRAIN_GET:
                return JumpWildState.FEATURE_ENABLE_TRAIN_LOOP;

            case JumpWildState.FEATURE_BJW_GET:
                return JumpWildState.FEATURE_BJW_LOOP;

            case JumpWildState.FEATURE_UPGRADE_GET:
                return JumpWildState.FEATURE_UPGRADE_LOOP;

            case JumpWildState.FEATURE_UPGRADE_TRIGGER:
                return JumpWildState.IDLE;

            case JumpWildState.FEATURE_6X6_GET:
                return JumpWildState.FEATURE_6X6_LOOP;

            case JumpWildState.FEATURE_MULT_GET:
                return JumpWildState.MERGE_IDLE;

            case JumpWildState.FEATURE_XWAYS_FS_GET:
                return JumpWildState.FEATURE_XWAYS_FS_LOOP;

            case JumpWildState.JUMP:
                return JumpWildState.IDLE;

            case JumpWildState.FALL_IN:
                return JumpWildState.IDLE;

            default:
                return state;
        }
    }
}

export class JumpingWildController extends PIXI.Container {
    protected readonly _state: JumpWildState = new JumpWildState(JumpWildState.IDLE);
    private _items: WildItem[] = [];
    private amountOfSkulls: number = 4;
    private _bottomTrainVisible: boolean = false;

    constructor() {
        super();
        this.name = "JumpingWildController";
        this.preCreateWilds();

        this.onResize();
    }

    public get isStillActive(): boolean {
        return !!this._items.find(w => w.visible)
    }

    public jumpToReset(): gsap.core.Timeline {
        let tl: gsap.core.Timeline = Helper.setTimeline();

        const durr: number = 0.6;

        this._items.forEach((item) => {
            if (item.visible) {
                tl.add(() => {
                    item.visible = false;
                    item.alpha = 0;
                    item.mult.text = "";
                    item.isBurning = false;
                    item.resetSpine();
                }, durr);
            }
        });
        return tl;
    }

    public makeAllDead(): void {
        this._items.forEach((item) => {
            this.playJWAnimation(item, JumpWildState.INACTIVE);
        });
    }

    public startShowJW(jwId: number, reelId: number, symId: number, isUpgrade: boolean = false, isRestore: boolean = false): void {
        if (isRestore && symId > 2) {
            if (symId === 3) {
                symId = 0
            } else if (symId === 4) {
                symId = 1
            } else {
                symId = 2
            }
        }

        const jw: WildItem = this._items[jwId];

        if (jwId === 3 && isUpgrade) {
            jw.position.set(this._items[symId].position.x, this._items[symId].position.y - 50);
            jw.scale.set(0.3);
        } else {
            jw.position = Helper.copyLocalPosition(jw, PREngineManager.getReel(reelId).getSymbol(symId));
        }
        jw.alpha = 1;
        jw.visible = true;

        // #354
        if (isRestore && jw.isBurning) {
            PunkRocker.BURNING_JUMPING_WILDS_COUNT = Math.min(4, ++PunkRocker.BURNING_JUMPING_WILDS_COUNT);
        }
    }

    public turnToBurning(jwId: number): void {
        const item: WildItem = this._items[jwId];

        // #354
        if (!item.isBurning) {
            PunkRocker.BURNING_JUMPING_WILDS_COUNT = Math.min(4, ++PunkRocker.BURNING_JUMPING_WILDS_COUNT);
            PunkRocker.background.setFrameAnim("skull_" + PunkRocker.BURNING_JUMPING_WILDS_COUNT, false, true);
        }

        item.isBurning = true;
    }

    public changeMultText(ID: number, text: string): void {
        const item: WildItem = this._items[ID];
        if (text.includes("X")) {
            item.mult.zIndex = parseFloat(text.split("X")[1]) * 100;
        }
        if (text.includes("+")) {
            item.mult.zIndex = parseFloat(text.split("+")[1]) * 100;
        }
        if (text.includes("W")) {
            item.mult.zIndex = 100;
        }
        item.mult.text = text.toLowerCase();
        item.mult.visible = true;
        item.mult.alpha = 0;
        gsap.to(item.mult, {
            alpha: 1,
            duration: GameConfig.JW_EXTRA_SPINS_SHOW_DELAY
        });
        item.sortChildren();
    }

    public changeMult(ID: number, multValue: number): void {
        const item: WildItem = this._items[ID];
        if ((item.isBurning)) {
            const jwMult: number = PunkRocker.model.gameData.raw.jumpingWildList[ID].previousMulti;

            if (jwMult > 1) {
                item.mult.text = "x" + jwMult;
            } else {
                item.mult.text = "";
            }
        } else {
            if (multValue.toString() != "1") {
                item.mult.text = multValue.toString() + "X";
                item.mult.zIndex = multValue * 100;
                this.sortChildren();
            } else if (multValue.toString() == "1" && item.mult.text.length > 0) {
                item.mult.text = "";
            }
        }
    }

    public playJumpAttentionHit(ID: number): gsap.core.Timeline {
        const item: WildItem = this._items[ID];
        return item.seq.play({
            onCompleteCallback: () => {
                item.seq.alpha = 0;
            },
            onStart: () => {
                item.seq.alpha = 1;
            }
        });
    }

    public PlayState(ID: number, state: string): gsap.core.Timeline {
        const item: WildItem = this._items[ID];

        item.mainSpine.visible = state != JumpWildState.INACTIVE;

        if (!item.mainSpine.visible) {
            return new gsap.core.Timeline();
        }
        if (!item.merged) {
            item.merged = (state === JumpWildState.MERGE
                || state === JumpWildState.FEATURE_PLUS_1_GET
                || state === JumpWildState.FEATURE_PLUS_1_GET
                || state === JumpWildState.FEATURE_MOLOTOV_FS_GET
                || state === JumpWildState.FEATURE_ENABLE_TRAIN_GET
                || state === JumpWildState.FEATURE_BJW_GET
                || state === JumpWildState.FEATURE_UPGRADE_GET
                || state === JumpWildState.FEATURE_6X6_GET);
        }

        if (item.merged && state === JumpWildState.WIN) {
            state = JumpWildState.MERGE_WIN;
        }

        if (item.merged && state === JumpWildState.IDLE) {
            state = JumpWildState.MERGE_IDLE;
        }

        item.timeline = new gsap.core.Timeline({
            onComplete: () => {
                if (state === JumpWildState.FEATURE_UPGRADE_TRIGGER) {
                    item.resetSpine(true);
                }
                this.playJWAnimation(item, this._state.NextSpineState(state));
            }
        });

        const durr: number = this._state.SpineStateDuration(state);

        item.timeline.add([
            () => {
                if (state === JumpWildState.FEATURE_PLUS_1_GET) {
                    const rawGameData: BaseServerData = PunkRocker.model.gameData.raw,
                        reelId: number = rawGameData.jumpingWildList[item.ID].newCoord.x,
                        topTrainFeatureOnReel: string = rawGameData.topTrain[reelId],
                        bottomTrainFeatureOnReel: string = rawGameData.bottomTrain[reelId];

                    if (topTrainFeatureOnReel === "PLUS_1" && bottomTrainFeatureOnReel === "PLUS_1") {
                        item.resetSpine(true);
                    }
                }
                this.playJWAnimation(item, state);
            },
            gsap.to(this, {duration: durr})
        ], 0)

        item.timeline.play();
        return item.timeline;
    }

    public JumpTo(conf: WildAnimationJump): gsap.core.Timeline {
        const item: WildItem = this._items[conf.ID],
            fromPosition: PIXI.Point = item.position;

        if (conf.to.reel === -1) {
            return Helper.setTimeline();
        } else {
            item.alpha = 1;
            item.visible = true;
        }

        if (NolimitApplication.isLandscape && this.position.x === 0 && this.position.y === 4) {
            const scaledReelDim: LayoutDimData = this._bottomTrainVisible ? PREngineManager.dimScaled : PREngineManager.dim;

            this.position.set(((scaledReelDim.scale.x === 1) ? 0 : 4), ((scaledReelDim.scale.y === 1) ? 4 : 20));
        }

        let toPosition: PIXI.Point = PunkRocker.reelEngineManager.getSymPos(conf.to.reel, conf.to.row);

        if (conf.ID === 3 && conf.from.reel === -1) {
            conf.state = JumpWildState.FALL_IN;
        }

        item.timeline = new gsap.core.Timeline({
            onStart: () => {
                item.jumped = false;
                item.merged = false;
                item.scale.set(1);
                item.resetSpine(true);
                this.playJWAnimation(item, conf.state);
            },
            onUpdate: () => {
                toPosition = PunkRocker.reelEngineManager.getSymPos(conf.to.reel, conf.to.row);
            },
            onComplete: () => {
                item.jumped = true;
                this.updateFinalJWPos(item.ID, conf.to.reel, conf.to.row, 0.001);
                this.playJWAnimation(item, this._state.NextSpineState(conf.state));
            }
        });

        const duration: number = conf.duration ? conf.duration : this._state.SpineStateDuration(conf.state);

        item.timeline.add([
            gsap.fromTo(item, {
                x: fromPosition.x,
                y: fromPosition.y
            }, {
                x: toPosition.x,
                y: toPosition.y,
                duration: duration
            }),
            () => {
                this.playJWAnimation(item, conf.state);
            }
        ], 0);
        item.timeline.play();
        return item.timeline;
    }

    public updateFinalJWPos(ID: number, reel: number, row: number, durr: number = 0): void {
        let item: WildItem = this._items[ID];

        gsap.to(item, {
            duration: durr,
            onUpdate: () => {
                if (!PREngineManager.getReel(reel).getSymbol(row)) {
                    row = row - 3;
                }

                const windowSize: number = PREngineManager.getReel(reel).windowConfAmount - 1;

                if (row > windowSize) {
                    //console.log("gggg onUpdate", windowSize, row, (row - this.getSizeToMinus(windowSize)));
                    row = row - this.getSizeToMinus(windowSize);
                }

                item.position = PunkRocker.reelEngineManager.getSymPos(reel, row);
            },
            onComplete: () => {
                if (!PREngineManager.getReel(reel).getSymbol(row)) {
                    row = row - 3;
                }

                const windowSize: number = PREngineManager.getReel(reel).windowConfAmount - 1;

                if (row > windowSize) {
                    //console.log("gggg onComplete", windowSize, row, (row - this.getSizeToMinus(windowSize)));
                    row = row - this.getSizeToMinus(windowSize);
                }

                item.position = PunkRocker.reelEngineManager.getSymPos(reel, row);
            }
        });
    }

    public updateSymbolPositions(ID: number, reel: number, row: number, durr: number = 0): void {
        let item = this._items[ID];

        gsap.to(item, {
            duration: durr,
            onUpdate: () => {
                item.position = PunkRocker.reelEngineManager.getSymPos(reel, row);
            }
        });

        this.PlayState(ID, JumpWildState.WIN);
    }

    public updateSymbolPosFor6x6(ID: number, reel: number, row: number, durr: number = 0): void {
        let item: WildItem = this._items[ID];

        gsap.to(item.position, {
            duration: durr,
            y: item.position.y + ((PunkRocker.reelEngineManager.getSymPos(reel, row).y) - item.position.y),
            onComplete: () => {
                item.position = PunkRocker.reelEngineManager.getSymPos(reel, row);
            }
        });

        this.PlayState(ID, JumpWildState.IDLE);
    }

    public ChangeToSpine(isRestoreState: boolean = false): void {
        this._items.forEach(item => {
            if (!item.mainSpine.visible) {
                const itemId: number = item.ID;

                item.mult.visible = true;
                item.mainSpine.visible = true;
                item.isBurning = this.getBurningStatusFromGameData(itemId);
                if (item.isBurning) {
                    const restoredMultValue: number = this.getMultValue(itemId, "new");
                    if (restoredMultValue > 1) {
                        this.changeMultText(itemId, ("x" + restoredMultValue));
                    }
                }

                this.playJWAnimation(item, JumpWildState.IDLE);

                if (isRestoreState) {
                    item.mainSpine.alpha = 1;
                } else {
                    gsap.fromTo(item.mainSpine, {alpha: 0}, {alpha: 1, duration: 0.25});
                }
            }
        })
    }

    public onBottomTrainShow(flag: boolean, isRestore: boolean = false): void {
        this._bottomTrainVisible = flag;
        this.onResize();
    }

    public onResize(): void {
        const scaledReelDim: LayoutDimData = this._bottomTrainVisible ? PREngineManager.dimScaled : PREngineManager.dim,
            jwList: jumpingWildConfig[] = PunkRocker.model.gameData.raw.jumpingWildList;

        // #388
        if (!jwList || !jwList.length) {
            return;
        }

        this._items.forEach((item) => {
            if (item.visible) {
                if (item.timeline) {
                    item.timeline.progress(1);
                }

                let currJWReelId: number,
                    currJWSymId: number;

                if (jwList[item.ID].newCoord.x === -1 || jwList[item.ID].newCoord.y === -1) {
                    currJWReelId = jwList[item.ID].previousCoord.x;
                    currJWSymId = jwList[item.ID].previousCoord.y;
                } else {
                    currJWReelId = jwList[item.ID].newCoord.x;
                    currJWSymId = jwList[item.ID].newCoord.y;
                }

                this.updateFinalJWPos(item.ID, currJWReelId, currJWSymId, 0.001);
            }
        });

        this.scale.set(scaledReelDim.scale.x, scaledReelDim.scale.y);

        if (NolimitApplication.isLandscape) {
            this.position.set(((scaledReelDim.scale.x === 1) ? 0 : 4), ((scaledReelDim.scale.y === 1) ? 4 : 20));
        } else {
            this.position.set(0, 4);
        }
    }

    public onFSIntroContinueClick(): void {
        this.position.set(0, 4);
    }

    public isBurningJW(jwId: number): boolean {
        return this._items[jwId].isBurning;
    }

    public upgradeHandlerFromMerge(jwId: number, animName: string): void {
        const item: WildItem = this._items[jwId],
            newTracks: string[] = [animName, JumpWildState.FEATURE_UPGRADE_LOOP];

        if (item.isBurning) {
            newTracks.push(JumpWildState.FEATURE_BJW_LOOP);
        }

        const allTracks: TrackEntry[] = item.mainSpine.state.tracks;

        item.resetSpine(true);

        for (let i: number = 0; i < newTracks.length; i++) {
            const trackName: string = newTracks[i],
                isLooped: boolean = this._state.isSpineLoop(trackName);

            item.mainSpine.state.addAnimation(allTracks.length, newTracks[i], isLooped, 0);
        }
    }

    public reArrangeWilds(): void {
        const gameData: GameData = PunkRocker.model.gameData,
            rawGameData: BaseServerData = gameData.raw,
            jwList: jumpingWildConfig[] = rawGameData.jumpingWildList,
            newOrderOfJW: number[] = [...Array(jwList.length).keys()];

        newOrderOfJW.sort((a: number, b: number): number => {
            if (jwList[a].reelChosen === jwList[b].reelChosen) {
                return jwList[a].newCoord.y - jwList[b].newCoord.y;
            } else {
                return jwList[a].reelChosen - jwList[b].reelChosen;
            }
        });

        for (let i: number = 0; i < newOrderOfJW.length; i++) {
            this.addChildAt(this._items[newOrderOfJW[i]], i);
        }
    }

    private getSizeToMinus(windowSize: number): number {
        let sizeToMinus: number = 0;

        if (windowSize === 2) {
            sizeToMinus = 3
        } else if (windowSize === 3) {
            sizeToMinus = 2
        } else if (windowSize === 4) {
            sizeToMinus = 1
        } else {
            sizeToMinus = 0
        }

        return sizeToMinus;
    }

    /*private printLog(str: string, itemId: number, allTracks: TrackEntry[]): void {
        const allTrackNames: string[] = [];
        for (let i = 0; i < allTracks.length; i++) {
            allTrackNames.push(allTracks[i].animation.name);
        }
        console.log(str, itemId, allTrackNames);
    }*/

    private playJWAnimation(item: WildItem, animName: string): void {
        const isLooped: boolean = this._state.isSpineLoop(animName),
            allTracks: TrackEntry[] = item.mainSpine.state.tracks;

        if (item.isBurning) {
            if (animName === JumpWildState.IDLE
                || animName === JumpWildState.JUMP
                || animName === JumpWildState.FALL_IN
                || animName === JumpWildState.MERGE_IDLE
            ) {
                animName = JumpWildState.FEATURE_BJW_LOOP;
            }
        }

        item.mainSpine.state.addAnimation(allTracks.length, animName, isLooped, 0);

        /*if(animName === JumpWildState.MERGE_IDLE) {
            for (let i: number = 0; i < allTracks.length; i++) {
                const currTrackInfo: TrackEntry = allTracks[i];

                if(currTrackInfo.animation.name === JumpWildState.FEATURE_UPGRADE_LOOP) {
                    item.mainSpine.state.addAnimation(allTracks.length, JumpWildState.FEATURE_UPGRADE_GET, isLooped, 0);
                    item.mainSpine.state.addAnimation(allTracks.length, JumpWildState.FEATURE_UPGRADE_LOOP, isLooped, 0);
                    break;
                }
            }
        }*/

        /*if (item.ID === 3) {
            this.printLog("JWWWW", item.ID, allTracks);
        }*/
    }

    private preCreateWilds(): void {
        for (let i = 0; i < this.amountOfSkulls; i++) {
            this._items.push(this.addChild(new WildItem(i)));
            this._items[i].alpha = 0;
            this._items[i].visible = false;
        }
    }

    private getBurningStatusFromGameData(jwId: number): boolean {
        const gameData: GameData = PunkRocker.model.gameData,
            jwList: jumpingWildConfig[] = gameData.raw.jumpingWildList;

        if (jwList[jwId]) {
            return jwList[jwId].isBurning;
        } else {
            return false;
        }
    }

    private getMultValue(jwId: number, type: string): number {
        const gameData: GameData = PunkRocker.model.gameData,
            jwList: jumpingWildConfig[] = gameData.raw.jumpingWildList;

        if (type === "prev") {
            return (jwList[jwId].previousMulti);
        } else {
            return jwList[jwId].newMultiplier;
        }
    }
}